import React from "react";

interface CardProps {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** `subtle`: nested or sidebar surface. `accent`: the one the reader holds. */
  variant?: "default" | "subtle" | "accent";
  /** Hover and focus-within raise the border to the accent; for a card that is a choice. */
  interactive?: boolean;
  padded?: boolean;
  /** Body grows to the card's height as a column, for equal-height rows. */
  fill?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const SURFACE: Record<NonNullable<CardProps["variant"]>, string> = {
  default: "card-surface",
  subtle: "card-surface-subtle",
  accent: "card-surface-accent",
};

/**
 * Surface with optional header/footer. Colours resolve through `--card-bg` /
 * `--card-border`, set by the surface class so `card-interactive` can move them.
 */
export const Card: React.FC<CardProps> = ({
  header,
  footer,
  children,
  variant = "default",
  interactive = false,
  padded = true,
  fill = false,
  className,
  style,
}) => {
  const bodyPadding = padded ? "var(--spacing-md)" : undefined;
  const classes = [
    SURFACE[variant],
    interactive ? "card-interactive" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      style={{
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        borderRadius: "var(--radius-app)",
        overflow: "hidden",
        ...(fill ? { display: "flex", flexDirection: "column" } : {}),
        ...style,
      }}
    >
      {header && (
        <div
          style={{
            padding: "var(--spacing-sm) var(--spacing-md)",
            borderBottom: "1px solid var(--card-border)",
            fontWeight: "var(--font-weight-semibold)",
          }}
        >
          {header}
        </div>
      )}
      <div
        style={{
          padding: bodyPadding,
          ...(fill
            ? {
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }
            : {}),
        }}
      >
        {children}
      </div>
      {footer && (
        <div
          style={{
            padding: "var(--spacing-sm) var(--spacing-md)",
            borderTop: "1px solid var(--card-border)",
            color: "var(--color-fg-muted)",
            fontSize: "var(--font-size-small)",
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};
